import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  ButtonLink,
  Column,
  ContentBox,
  ContentPage,
  CopyIcon,
  DescriptionList,
  InputText,
  ModalForm,
  NoResults,
  Notice,
  PageActions,
  Row,
  Skeleton,
  Text,
  copyToClipboard,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { canOpenScreen } from "@helpers/companyScreens";
import { formatPhone } from "@helpers";
import RemoveEmployeeModal from "@components/RemoveEmployeeModal";
import { DateText, DocumentNumber, DomainStatus, formatDocument, usePageHeader } from "@components/ow";
import { CONTRACT_STATUS, remunerationSummary } from "../../Common/ContractDetail";
import { focusFirstError } from "../../Common/FormPage";
import LinkStatus from "@components/LinkStatus";

// Detalhe do colaborador (modelo Detalhe do DS OneWork, sem abas): ações da página
// no PageActions e os blocos em duas colunas, com os contratos embaixo. Editar e
// remover só para o ADM; "Ver colaboradores" lê.

const LIST_HREF = "/c/employees";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ERP_MAX = 100;
const EDIT_FIELDS = ["employee-job", "employee-corporate-email", "employee-erp"];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "20px"};
`;

const Inline = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

// Mesmo botão de ícone do "revelar" do DocumentNumber
const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50px;
  color: ${({ theme }) => theme.colors.N400};
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.N100};
    color: ${({ theme }) => theme.colors.N900};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.FOCUS};
  }
`;

/** Valor com o botão de copiar ao lado (documento, e-mail, telefone, PIX, conta). */
function CopyValue({ text, label, children }) {
  return (
    <Inline>
      {children || <span>{text}</span>}
      <CopyButton
        type="button"
        title={`Copiar ${label}`}
        aria-label={`Copiar ${label}`}
        onClick={() => copyToClipboard(text, "Copiado!")}
      >
        <CopyIcon />
      </CopyButton>
    </Inline>
  );
}

/** Linhas do bloco "Dados bancários", só as que têm valor. */
function bankItems(bank) {
  const account = bank.bank_account ? [bank.bank_account, bank.bank_account_digit].filter(Boolean).join("-") : "";
  const items = [];
  if (bank.pix_key) items.push({ label: "Chave PIX", value: <CopyValue text={bank.pix_key} label="chave PIX" /> });
  if (bank.bank_name) {
    items.push({ label: "Banco", value: bank.bank_code ? `${bank.bank_name} (${bank.bank_code})` : bank.bank_name });
  }
  if (bank.bank_agency) items.push({ label: "Agência", value: bank.bank_agency });
  if (account) items.push({ label: "Conta", value: <CopyValue text={account} label="conta" /> });
  return items;
}

function validateEdit(values) {
  const errors = {};
  const email = values.corporateEmail.trim();
  if (email && !EMAIL_REGEX.test(email)) errors["employee-corporate-email"] = "E-mail corporativo inválido";
  if (values.erpCode.trim().length > ERP_MAX) errors["employee-erp"] = `Até ${ERP_MAX} caracteres`;
  return errors;
}

function DetailSkeleton() {
  return (
    <ContentPage aria-busy="true">
      <ContentBox>
        <Stack $gap="24px">
          <Row $gap={20} $alignItems="flex-start">
            <Column $columns={6}>
              <Stack>
                <Skeleton $height={120} />
                <Skeleton $height={120} />
              </Stack>
            </Column>
            <Column $columns={6}>
              <Stack>
                <Skeleton $height={120} />
                <Skeleton $height={120} />
              </Stack>
            </Column>
          </Row>
          <Skeleton $height={160} />
        </Stack>
      </ContentBox>
    </ContentPage>
  );
}

export default function EmployeeDetails() {
  const params = useParams();
  const navigate = useNavigate();
  const employeeService = useService("users");
  const contractService = useService("contracts");
  const user = JSON.parse(localStorage.getItem("user_ow") || "{}");
  // "Ver colaboradores" só lê: editar e remover seguem só ADM
  const isAdmin = user?.role === "ADM";
  const canReadContracts = canOpenScreen("contracts", user);

  const [data, setData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRemove, setShowRemove] = useState(false);

  // Editar
  const [editValues, setEditValues] = useState(null);
  const [editSubmitted, setEditSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const name = data?.name || "Colaborador";
  // O Layout escapa o título; o breadcrumb é texto
  usePageHeader({
    title: name,
    breadcrumb: [
      { page: "Colaboradores", route: LIST_HREF },
      { page: name, route: `/c/employe/${params.id}` },
    ],
  });

  useEffect(() => {
    fetchEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  async function fetchEmployee() {
    try {
      // Contratos para o ADM ou com "Ver contratos"
      const [employeeResult, contractsResult] = await Promise.all([
        employeeService.get(params.id),
        canReadContracts ? contractService.list(`scope=company&user_id=${params.id}&limit=9999`) : null,
      ]);
      if (!employeeResult?.data) throw new Error("Colaborador não encontrado");
      setData(employeeResult.data);
      setContracts(contractsResult?.data?.list || []);
      setLoading(false);
    } catch (error) {
      console.error(error);
      const status = error?.response?.status;
      // toastId: o StrictMode do dev busca duas vezes
      toast.error(
        status === 403 || status === 404 ? "Você não tem acesso a este colaborador" : "Erro ao carregar o colaborador",
        { toastId: "employee-load" }
      );
      navigate(LIST_HREF);
    }
  }

  function openEdit() {
    setEditValues({
      job: data.job || "",
      corporateEmail: data.corporate_email || "",
      erpCode: data.erp_code || "",
    });
    setEditSubmitted(false);
  }

  function closeEdit() {
    if (!saving) setEditValues(null);
  }

  async function handleSave() {
    setEditSubmitted(true);
    const errors = validateEdit(editValues);
    if (Object.keys(errors).length) {
      focusFirstError(EDIT_FIELDS.filter((id) => errors[id]));
      return;
    }

    const email = editValues.corporateEmail.trim();
    const erpCode = editValues.erpCode.trim();
    const payload = {
      job: editValues.job.trim(),
      corporate_email: email ? email.toLowerCase() : null,
      erp_code: erpCode || null,
    };

    setSaving(true);
    try {
      const response = await employeeService.conn.axios.put(`/users/${params.id}`, payload);
      if (response?.data?.error) {
        toast.error(response.data.msg || "Erro ao atualizar dados");
      } else {
        setData((prev) => ({ ...prev, ...payload }));
        setEditValues(null);
        toast.success("Dados atualizados!");
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Erro ao atualizar dados");
    }
    setSaving(false);
  }

  if (loading) return <DetailSkeleton />;

  const phoneDigits = (data.phone || "").replace(/\D/g, "");
  const activeContracts = contracts.filter((c) => c.status === "ACTIVE" || c.status === "PAUSED").length;
  const editErrors = editSubmitted && editValues ? validateEdit(editValues) : {};

  const contractColumns = [
    { id: "project", header: "Projeto", size: 280, cell: ({ row }) => row.original.project?.name || "Sem projeto" },
    { id: "remuneration", header: "Remuneração", size: 280, cell: ({ row }) => remunerationSummary(row.original) },
    {
      id: "status",
      header: "Status",
      size: 140,
      cell: ({ row }) => <DomainStatus domain="contrato" value={CONTRACT_STATUS[row.original.status] || row.original.status} />,
    },
    {
      id: "start",
      header: "Início",
      size: 140,
      cell: ({ row }) => (row.original.start_date ? <DateText value={row.original.start_date} /> : "—"),
    },
  ];

  return (
    <ContentPage>
      {isAdmin ? (
        <PageActions>
          <Button $text="Editar dados" $variant="outline" $action={openEdit} />
          <Button $text="Remover colaborador" $variant="outline_danger" $action={() => setShowRemove(true)} />
        </PageActions>
      ) : null}

      <ContentBox>
        <Stack $gap="24px">
          <Row $gap={20} $alignItems="flex-start">
            <Column $columns={6}>
              <Stack>
                <DescriptionList
                  $title="Colaborador"
                  $items={[
                    { label: "Situação", value: <LinkStatus status={data.status} /> },
                    {
                      label: "Documento",
                      value: data.document ? (
                        <CopyValue text={formatDocument(data.document)} label="documento">
                          <DocumentNumber value={data.document} />
                        </CopyValue>
                      ) : null,
                    },
                  ]}
                />
                <DescriptionList
                  $title="Vínculo com a empresa"
                  $items={[
                    { label: "Cargo", value: data.job },
                    { label: "E-mail corporativo", value: data.corporate_email },
                    { label: "Código ERP", value: data.erp_code },
                  ]}
                />
              </Stack>
            </Column>
            <Column $columns={6}>
              <Stack>
                <DescriptionList
                  $title="Contato"
                  $items={[
                    { label: "E-mail", value: data.email ? <CopyValue text={data.email} label="e-mail" /> : null },
                    {
                      label: "WhatsApp",
                      value: phoneDigits ? (
                        <CopyValue text={formatPhone(data.phone)} label="telefone">
                          <ButtonLink
                            $text={formatPhone(data.phone)}
                            $action={`https://wa.me/55${phoneDigits}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            $noPadding
                            $noWidth
                          />
                        </CopyValue>
                      ) : null,
                    },
                  ]}
                />
                {/* O back só manda bankData para o ADM, quem tem "Ver dados bancários" e a própria pessoa */}
                {"bankData" in data ? <BankBlock bank={data.bankData} /> : null}
              </Stack>
            </Column>
          </Row>

          {canReadContracts ? (
            <Stack $gap="12px">
              <Text $text="Contratos" $variant="caption_bold" $color="N700" />
              <BasicTable
                data={contracts}
                columns={contractColumns}
                getRowId={(row) => String(row.id)}
                onRowClick={(contract) => navigate(`/c/contracts/${contract.id}`)}
                noResults={<NoResults notResultText="Nenhum contrato com este colaborador" />}
              />
            </Stack>
          ) : null}
        </Stack>
      </ContentBox>

      {editValues && (
        <ModalForm
          $showModal={!!editValues}
          $setShowModal={closeEdit}
          $noClose={saving}
          $isLoading={saving}
          $size="medium"
          $title="Editar dados"
          $contentBackground="N0"
          $actions={[
            { text: "Cancelar", action: closeEdit, variant: "outline", isDisabled: saving },
            { text: "Salvar alterações", action: handleSave, variant: "primary", isDisabled: saving },
          ]}
        >
          <Stack>
            <InputText
              $idForm="employee-job"
              $label="Cargo"
              $variant="default"
              placeholder="Ex.: Desenvolvedor"
              value={editValues.job}
              onChange={(e) => setEditValues((prev) => ({ ...prev, job: e.target.value }))}
            />
            <InputText
              $idForm="employee-corporate-email"
              $label="E-mail corporativo"
              $variant="default"
              $errors={editErrors["employee-corporate-email"]}
              type="email"
              placeholder="voce@empresa.com.br"
              value={editValues.corporateEmail}
              onChange={(e) => setEditValues((prev) => ({ ...prev, corporateEmail: e.target.value }))}
            />
            <InputText
              $idForm="employee-erp"
              $label="Código ERP"
              $variant="default"
              $errors={editErrors["employee-erp"]}
              maxLength={ERP_MAX}
              placeholder="Ex.: ERP-001"
              value={editValues.erpCode}
              onChange={(e) => setEditValues((prev) => ({ ...prev, erpCode: e.target.value }))}
            />
          </Stack>
        </ModalForm>
      )}

      {showRemove ? (
        <RemoveEmployeeModal
          employee={data}
          activeContracts={activeContracts}
          onClose={() => setShowRemove(false)}
          onRemoved={() => navigate(LIST_HREF)}
        />
      ) : null}
    </ContentPage>
  );
}

/** Dados bancários; sem PIX e sem conta, o aviso (como no drawer da nota fiscal). */
function BankBlock({ bank }) {
  if (!bank || (!bank.pix_key && !bank.bank_account)) {
    return <Notice $variant="gray" $title="Dados bancários" $text="O colaborador ainda não cadastrou os dados bancários." />;
  }
  return <DescriptionList $title="Dados bancários" $items={bankItems(bank)} />;
}
